import { defineStore } from 'pinia';
import { ref } from 'vue';
import { omniInvoke, omniListen } from '../utils/bridge';

export interface ActiveCall {
    peerDid: string;
    roomId: string;
    type: 'audio' | 'video';
    direction: 'incoming' | 'outgoing';
    status: 'ringing' | 'connected' | 'ended';
}

export const useAvStore = defineStore('avStore', () => {
    const activeCall = ref<ActiveCall | null>(null);
    const localToken = ref<string | null>(null);
    const serverUrl = ref<string | null>(null);

    // Call Actions
    const startCall = async (targetDid: string, type: 'audio' | 'video') => {
        const roomId = `call_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
        activeCall.value = {
            peerDid: targetDid,
            roomId,
            type,
            direction: 'outgoing',
            status: 'ringing'
        };

        // Send P2P signal
        await omniInvoke('swarm_send_message', {
            targetDid,
            content: JSON.stringify({ roomId, type }),
            msgType: 'av_call_invite'
        });
    };

    const acceptCall = async (participantName: string) => {
        if (!activeCall.value) return;
        
        activeCall.value.status = 'connected';
        // Notify peer
        await omniInvoke('swarm_send_message', {
            targetDid: activeCall.value.peerDid,
            content: JSON.stringify({ roomId: activeCall.value.roomId }),
            msgType: 'av_call_accept'
        });

        // Request LiveKit Token
        try {
            const res = await omniInvoke('livekit_get_connection_token', {
                roomName: activeCall.value.roomId,
                participantName,
                isAdmin: false
            }) as any;
            localToken.value = res.token;
            serverUrl.value = res.server_url;
        } catch (e) {
            console.error('Failed to get LiveKit token:', e);
            endCall();
        }
    };

    const rejectCall = async () => {
        if (!activeCall.value) return;
        await omniInvoke('swarm_send_message', {
            targetDid: activeCall.value.peerDid,
            content: JSON.stringify({ roomId: activeCall.value.roomId }),
            msgType: 'av_call_reject'
        });
        activeCall.value = null;
    };

    const endCall = async () => {
        if (activeCall.value && activeCall.value.status === 'connected') {
            await omniInvoke('swarm_send_message', {
                targetDid: activeCall.value.peerDid,
                content: JSON.stringify({ roomId: activeCall.value.roomId }),
                msgType: 'av_call_end'
            });
        }
        activeCall.value = null;
        localToken.value = null;
        serverUrl.value = null;
    };

    const startGroupCall = async (groupId: string, type: 'audio' | 'video', participantName: string) => {
        const roomId = `group_call_${groupId}_${Date.now()}`;
        activeCall.value = {
            peerDid: groupId,
            roomId,
            type,
            direction: 'outgoing',
            status: 'connected' // Initiator connects immediately
        };

        // Broadcast to group
        await omniInvoke('swarm_send_group_message', {
            group_id: groupId,
            content: JSON.stringify({ roomId, type }),
            msg_type: 'av_group_invite'
        });

        // Request LiveKit Token
        try {
            const res = await omniInvoke('livekit_get_connection_token', {
                roomName: roomId,
                participantName,
                isAdmin: false
            }) as any;
            localToken.value = res.token;
            serverUrl.value = res.server_url;
        } catch (e) {
            console.error('Failed to get LiveKit token for group call:', e);
            endCall();
        }
    };

    const handleIncomingSignal = async (payload: any, localIdentityName: string) => {
        const msgType = payload.msg_type;
        const senderDid = payload.sender_did;
        
        try {
            const content = JSON.parse(payload.content);
            if (msgType === 'av_call_invite') {
                if (activeCall.value) {
                    // Busy, auto-reject
                    await omniInvoke('swarm_send_message', {
                        targetDid: senderDid,
                        content: JSON.stringify({ roomId: content.roomId, reason: 'busy' }),
                        msgType: 'av_call_reject'
                    });
                    return;
                }
                activeCall.value = {
                    peerDid: senderDid,
                    roomId: content.roomId,
                    type: content.type,
                    direction: 'incoming',
                    status: 'ringing'
                };
            } else if (msgType === 'av_group_invite') {
                // For group invites, we show it as ringing but the accept process is different.
                if (activeCall.value) return; // Busy
                activeCall.value = {
                    peerDid: senderDid, // Could be the group ID depending on how payload is constructed, but sender is fine for UI
                    roomId: content.roomId,
                    type: content.type,
                    direction: 'incoming',
                    status: 'ringing'
                };
            } else if (msgType === 'av_call_accept') {
                if (activeCall.value && activeCall.value.roomId === content.roomId && activeCall.value.status === 'ringing') {
                    activeCall.value.status = 'connected';
                    // Fetch token
                    const res = await omniInvoke('livekit_get_connection_token', {
                        roomName: activeCall.value.roomId,
                        participantName: localIdentityName,
                        isAdmin: false
                    }) as any;
                    localToken.value = res.token;
                    serverUrl.value = res.server_url;
                }
            } else if (msgType === 'av_call_reject' || msgType === 'av_call_end') {
                if (activeCall.value && activeCall.value.roomId === content.roomId) {
                    activeCall.value = null;
                    localToken.value = null;
                    serverUrl.value = null;
                }
            }
        } catch (e) {
            console.error('Failed to parse AV signal:', e);
        }
    };

    // Override acceptCall for group calls
    const originalAcceptCall = acceptCall;
    const smartAcceptCall = async (participantName: string) => {
        if (!activeCall.value) return;
        if (activeCall.value.roomId.startsWith('group_call_')) {
            activeCall.value.status = 'connected';
            // Just get token and join, no need to send accept signal to an individual
            try {
                const res = await omniInvoke('livekit_get_connection_token', {
                    roomName: activeCall.value.roomId,
                    participantName,
                    isAdmin: false
                }) as any;
                localToken.value = res.token;
                serverUrl.value = res.server_url;
            } catch (e) {
                console.error('Failed to join group call:', e);
                endCall();
            }
        } else {
            await originalAcceptCall(participantName);
        }
    };

    return {
        activeCall,
        localToken,
        serverUrl,
        startCall,
        startGroupCall,
        acceptCall: smartAcceptCall,
        rejectCall,
        endCall,
        handleIncomingSignal
    };
});
